import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, User, MessageSquare, Phone, MapPin, Check, AlertCircle, Loader2 } from "lucide-react";



import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    setSuccess(false);

    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="contact-page">
      
      

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-b from-zinc-900 to-black">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black uppercase text-white tracking-wider mb-4">
              Contact Us
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Have questions, feedback, or partnership inquiries? We'd love to hear from you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase text-white tracking-wide mb-4">
                  Get In Touch
                </h2>
                <p className="text-zinc-400">
                  Whether you're interested in advertising, partnerships, or just want to say hello, 
                  fill out the form and we'll get back to you as soon as possible.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Email</h3>
                    <p className="text-zinc-400">antlersportsnetwork@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Phone</h3>
                    <p className="text-zinc-400">(817) 807-8753</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Location</h3>
                    <p className="text-zinc-400">Arlington, Texas</p>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="pt-8 border-t border-white/10">
                <h3 className="text-white font-bold mb-4">Quick Links</h3>
                <div className="grid grid-cols-2 gap-3">
                  <a href="/careers" className="text-zinc-400 hover:text-orange-500 transition-colors text-sm">
                    → Careers
                  </a>
                  <a href="/news" className="text-zinc-400 hover:text-orange-500 transition-colors text-sm">
                    → News
                  </a>
                  <a href="/schedule" className="text-zinc-400 hover:text-orange-500 transition-colors text-sm">
                    → Schedule
                  </a>
                  <a href="/on-demand" className="text-zinc-400 hover:text-orange-500 transition-colors text-sm">
                    → On Demand
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="bg-zinc-900/50 border border-white/10 p-6 md:p-8">
                <h3 className="font-heading text-xl font-bold uppercase text-white tracking-wide mb-6">
                  Send a Message
                </h3>

                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 mb-6"
                  >
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-500">Message sent successfully! We'll be in touch soon.</span>
                  </motion.div>
                )}

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 mb-6"
                  >
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-red-500">{error}</span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-400 mb-2 tracking-wide">
                        <User className="w-3 h-3 inline mr-1" /> Name *
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Your name"
                        required
                        className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-400 mb-2 tracking-wide">
                        <Mail className="w-3 h-3 inline mr-1" /> Email *
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="your@email.com"
                        required
                        className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-400 mb-2 tracking-wide">
                        <Phone className="w-3 h-3 inline mr-1" /> Phone
                      </label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-400 mb-2 tracking-wide">
                        Subject *
                      </label>
                      <select
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        required
                        className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors"
                        data-testid="contact-subject"
                      >
                        <option value="">Select a subject</option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Advertising">Advertising</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Technical Support">Technical Support</option>
                        <option value="Press & Media">Press & Media</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-400 mb-2 tracking-wide">
                      <MessageSquare className="w-3 h-3 inline mr-1" /> Message *
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="How can we help you?"
                      required
                      rows={5}
                      className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500 transition-colors resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-orange-500 text-black font-heading text-sm font-bold uppercase tracking-wider py-4 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    data-testid="contact-submit"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      
    </div>
  );
};

export default ContactPage;
